// SVG half-court that captures tap-to-record shot locations.
// Coordinates emitted in normalised [0..1] x/y for storage in player_game_stats.shots.
// Click toggles `pendingShot` in parent; parent then chooses made/missed via a confirm tray.
import { useRef, useState } from "react";

const W = 500;          // SVG viewport width
const H = 470;          // half-court height (slightly taller than half real-court for legibility)
const COURT_STROKE = "#A1A1AA";

export default function BasketballCourt({ shots = [], onShot, disabled = false }) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);

  const getNormalizedPoint = (clientX, clientY) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;

    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    return {
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    };
  };

  const handleClick = (e) => {
    if (disabled || !onShot) return;
    const point = getNormalizedPoint(e.clientX, e.clientY);
    if (!point) return;

    // Detect 3-point shot: outside the arc (rough geometric test)
    const dx = point.x - 0.5;
    const dy = point.y - 0.06; // hoop at top of half-court
    const dist = Math.sqrt(dx * dx + dy * dy);
    const is3 = dist > 0.48 || point.y > 0.78;
    onShot({ x: point.x, y: point.y, type: is3 ? "3pt" : "2pt" });
  };

  return (
    <div className="relative w-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto select-none cursor-crosshair"
        onClick={handleClick}
        onMouseMove={(e) => {
          if (disabled) return;
          const point = getNormalizedPoint(e.clientX, e.clientY);
          if (!point) return;
          setHover({ x: point.x * W, y: point.y * H });
        }}
        onMouseLeave={() => setHover(null)}
        data-testid="court-svg"
        role="img"
        aria-label="Basketball half-court shot chart"
      >
        {/* hardwood backdrop */}
        <rect x="0" y="0" width={W} height={H} fill="#121212" />

        {/* outer boundary */}
        <rect x="10" y="10" width={W - 20} height={H - 20} fill="none" stroke={COURT_STROKE} strokeOpacity="0.4" strokeWidth="2" />

        {/* paint (key) */}
        <rect x={(W - 160) / 2} y="10" width="160" height="190" fill="#ffffff" fillOpacity="0.04" stroke={COURT_STROKE} strokeOpacity="0.4" strokeWidth="2" />

        {/* free-throw line + circle */}
        <line x1={(W - 160) / 2} y1="200" x2={(W + 160) / 2} y2="200" stroke={COURT_STROKE} strokeOpacity="0.4" strokeWidth="2" />
        <circle cx={W / 2} cy="200" r="60" fill="none" stroke={COURT_STROKE} strokeOpacity="0.4" strokeWidth="2" />

        {/* restricted area (small arc under the basket) */}
        <path d={`M ${W / 2 - 40} 10 A 40 40 0 0 0 ${W / 2 + 40} 10`} fill="none" stroke={COURT_STROKE} strokeOpacity="0.4" strokeWidth="2" />

        {/* 3-point arc */}
        <path
          d={`M 40 10 L 40 80 A 210 210 0 0 0 ${W - 40} 80 L ${W - 40} 10`}
          fill="none"
          stroke="#FF4500"
          strokeOpacity="0.5"
          strokeWidth="2.5"
        />

        {/* hoop */}
        <circle cx={W / 2} cy="22" r="9" fill="none" stroke="#FF4500" strokeWidth="2.5" />
        <rect x={W / 2 - 30} y="10" width="60" height="3" fill="#FF4500" />

        {/* center "logo" mark */}
        <text x={W / 2} y={H - 30} textAnchor="middle" fontFamily="Barlow Condensed" fontWeight="900" fontSize="40" fill="#ffffff" fillOpacity="0.05" letterSpacing="6">HALF COURT</text>

        {/* existing shots */}
        {shots.map((s) => (
          <g key={`${s.ts ?? `${s.x}-${s.y}`}-${s.made ? "m" : "x"}`}>
            {s.made ? (
              <circle cx={s.x * W} cy={s.y * H} r="7" fill="#39FF14" stroke="#000" strokeWidth="1.5" />
            ) : (
              <g>
                <line x1={s.x * W - 6} y1={s.y * H - 6} x2={s.x * W + 6} y2={s.y * H + 6} stroke="#FF003C" strokeWidth="3" />
                <line x1={s.x * W + 6} y1={s.y * H - 6} x2={s.x * W - 6} y2={s.y * H + 6} stroke="#FF003C" strokeWidth="3" />
              </g>
            )}
          </g>
        ))}

        {/* hover indicator */}
        {hover && !disabled && (
          <circle cx={hover.x} cy={hover.y} r="10" fill="none" stroke="#FF4500" strokeWidth="1.5" strokeDasharray="3 3" />
        )}
      </svg>

      <div className="absolute bottom-2 left-2 flex gap-3 text-[10px] font-bold tracking-widest uppercase text-zinc-400">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-neon rounded-full" />Made</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-foul rounded-full" />Missed</span>
      </div>
    </div>
  );
}
